import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { fetchActiveCategories, fetchTrendingArticles, fetchOnThisDay } from "../services/api";
import { useAuth } from "./AuthContext";
import { Calendar, Eye } from "lucide-react";

const SPORT_COLORS = {
  "AFL": "#1B7A3D",
  "NRL": "#E65100",
  "Cricket": "#D4A017",
  "Football": "#1565C0",
  "Tennis": "#4E9A06",
  "Rugby": "#5B2C6F",
  "NBA": "#1D428A",
  "F1": "#E10600",
  "Boxing": "#C41E3A",
  "Athletics": "#0277BD",
  "Olympics": "#0085C7",
  "Golf": "#2E7D32",
  "General": "#6B7280",
  "Horse Racing": "#8B4513",
  "Motorsport": "#FF6F00",
  "NFL": "#013369",
  "Ice Hockey": "#041E42",
  "Rowing": "#00695C",
  "Baseball": "#002D62",
};

const Sidebar = ({ categories: propCategories, showOnThisDay = false }) => {
  const { user } = useAuth();
  const [activeCategories, setActiveCategories] = useState([]);
  const [mostRead, setMostRead] = useState([]);
  const [onThisDay, setOnThisDay] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [cats, trending, otd] = await Promise.all([
          fetchActiveCategories(),
          fetchTrendingArticles(5),
          showOnThisDay ? fetchOnThisDay() : Promise.resolve(null),
        ]);
        setActiveCategories(cats);
        setMostRead(trending);
        setOnThisDay(otd);
      } catch (err) {
        console.error("Failed to load sidebar data:", err);
      }
    };
    load();
  }, []);

  const getCategoryInfo = (categoryId) => {
    return (propCategories || activeCategories).find((c) => c.id === categoryId);
  };

  return (
    <aside className="space-y-8">
      {/* On This Day in Sport */}
      {onThisDay && onThisDay.events && onThisDay.events.length > 0 && (
        <div data-testid="on-this-day-section" className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-1 h-6 bg-[#1B7A3D] rounded-full" />
            <h3 className="text-lg font-bold text-gray-900">This Day in Sport</h3>
          </div>
          <p className="text-xs text-gray-400 font-medium mb-5 ml-3">{onThisDay.date}</p>
          <div className="space-y-4">
            {onThisDay.events.map((evt, index) => (
              <div
                key={index}
                data-testid={`on-this-day-item-${index}`}
                className="relative pl-4 border-l-2 transition-colors"
                style={{ borderColor: SPORT_COLORS[evt.sport] || "#6B7280" }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                    style={{
                      color: SPORT_COLORS[evt.sport] || "#6B7280",
                      backgroundColor: `${SPORT_COLORS[evt.sport] || "#6B7280"}15`,
                    }}
                  >
                    {evt.sport}
                  </span>
                  <span className="text-[10px] text-gray-400 font-medium">{evt.year}</span>
                </div>
                <p className="text-sm text-gray-700 leading-snug">{evt.event}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Trending Now / Most Read */}
      {mostRead.length > 0 && (
        <div data-testid="most-read-section" className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-1 h-6 bg-[#1B7A3D] rounded-full" />
            <h3 className="text-lg font-bold text-gray-900">Trending Now</h3>
          </div>
          <div className="space-y-4">
            {mostRead.map((article, index) => {
              const cat = getCategoryInfo(article.category);
              return (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  data-testid={`most-read-item-${index}`}
                  className="group flex gap-3 items-start"
                >
                  <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-sm font-bold text-gray-400 group-hover:bg-[#1B7A3D] group-hover:text-white transition-all duration-200">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div className="flex-1 min-w-0">
                    <span
                      className="text-[10px] font-bold uppercase tracking-wider"
                      style={{ color: cat?.color || "#1B7A3D" }}
                    >
                      {cat?.name}
                    </span>
                    <h4 className="text-sm font-semibold text-gray-900 line-clamp-2 mt-0.5 group-hover:text-[#1B7A3D] transition-colors">
                      {article.title}
                    </h4>
                    {(user || article.views >= 100) && (
                      <div className="flex items-center gap-1 mt-1">
                        <Eye size={10} className="text-gray-400" />
                        <span className="text-[10px] text-gray-400">{article.views.toLocaleString()} views</span>
                      </div>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
